import { getFirstTwoInitials } from '@Sage/ssm-utils';
import ColorHash from 'color-hash';

import TagElement from '../TagElement';
const colorHash = new ColorHash();

let srcPlaceholders = {};
let srcHasPlaceholder = {};
let existingPlaceholders = {};

export default function Avatar(params) {
    const {
        data,
        otherFields,
        isSquared,
        imageSize,
        value,
        innerMethods,
        reverse,
        onlyIcon,
        otherParams,
    } = params;

    const avatarCell = document.createElement('div');
    avatarCell.classList.add('w5-avatar-cell');
    if (reverse) avatarCell.classList.add('w5-avatar-cell--reverse');
    if (!params.value) {
        if (params.colDef.cellRendererParams.withEmptyPlaceholder) {
            const textContainer = document.createElement('div');
            textContainer.classList.add('w5-avatar-cell-text');
            textContainer.classList.add('w5-text-cell__placeholder');
            textContainer.innerText = params.t('label_placeholder_unamed_record');
            avatarCell.appendChild(textContainer);
        }
        return avatarCell;
    }

    const text = value || '-';
    const src = data[otherFields?.src] || '';
    const placeholder = data[otherFields?.placeholder] || '';
    const alt = data[otherFields?.alt] || '';

    let svgContainer = null;
    let pngElement = null;
    const svg = (innerMethods?.getSvg && innerMethods.getSvg(data)) || null;
    const png = (innerMethods?.getPng && innerMethods.getPng(data.id)) || null;

    if (svg) {
        svgContainer = document.createElement('div');
        svgContainer.classList.add('w5-avatar-cell__icon');
        svgContainer.innerHTML = svg;
    }
    const textContainer = document.createElement('div');
    textContainer.classList.add('w5-avatar-cell-text');
    if ((!placeholder && !src && !innerMethods?.getAvatarSvg && !innerMethods?.getPng) || !text) {
        textContainer.innerText = text;
        avatarCell.appendChild(textContainer);
        return avatarCell;
    }

    if (innerMethods?.getUrl && !params.onClick) {
        const linkElement = document.createElement('a');
        linkElement.href = innerMethods.getUrl(data);
        linkElement.innerText = text;
        textContainer.appendChild(linkElement);
    } else textContainer.innerText = text;

    let wrapperTextContainer = null;
    if (otherFields?.secondaryTextKey) {
        wrapperTextContainer = document.createElement('div');
        wrapperTextContainer.classList.add('w5-avatar-cell__text-wrapper');

        const secondaryTextContainer = document.createElement('div');
        secondaryTextContainer.classList.add('w5-avatar-cell-text--secondary');
        secondaryTextContainer.innerText = data[otherFields.secondaryTextKey] || '';

        wrapperTextContainer.appendChild(textContainer);
        wrapperTextContainer.appendChild(secondaryTextContainer);
    }

    const imgContainer = document.createElement('div');
    imgContainer.classList.add('w5-avatar-cell__img-container');
    const imgBox = document.createElement('div');
    imgBox.classList.add('w5-avatar-cell__img-box');

    if (png) {
        pngElement = document.createElement('img');
        pngElement.classList.add('w5-avatar-cell-png--avatar');
        imgBox.classList.add('w5-avatar-cell__img-box--png');
        if (otherParams.initials) {
            let name = otherParams.initials(data);
            let initials = getFirstTwoInitials(name);
            let initialsElement = document.createElement('span');
            imgBox.style.backgroundColor = colorHash.hex(initials);
            initialsElement.innerText = initials;
            initialsElement.classList.add('w5-avatar-cell__img-box--initials');
            imgBox.appendChild(initialsElement);
        }
        innerMethods.getPng(data.id).then(({ data: user }) => {
            if (user.photo) {
                imgBox.removeChild(imgBox.childNodes[0]);
                pngElement.src = `data:image/png;base64, ${user.photo}`;
            }
        });
    }
    if (isSquared) imgBox.classList.add('w5-avatar-cell__img-box--squared');
    if (onlyIcon) imgBox.classList.add('w5-avatar-cell__img-box--only-icon');
    if (imageSize) {
        // hoi-poi support this sizes: 'small', 'medium', 'large', 'big', 'huge'
        switch (imageSize) {
            case 'small':
                avatarCell.classList.add('w5-avatar-cell--small');
                imgBox.classList.add('w5-avatar-cell__img-box--small');
                break;
            case 'big':
                avatarCell.classList.add('w5-avatar-cell--big');
                imgBox.classList.add('w5-avatar-cell__img-box--big');
                break;
            case 'medium':
            default:
                avatarCell.classList.add('w5-avatar-cell--medium');
                imgBox.classList.add('w5-avatar-cell__img-box--medium');
                break;
        }
    } else {
        avatarCell.classList.add('w5-avatar-cell--medium');
        imgBox.classList.add('w5-avatar-cell__img-box--medium');
    }

    const appendNewImageParams = {
        imgContainer,
        pngElement,
        imgBox,
        svgContainer,
        avatarCell,
        textContainer: wrapperTextContainer || textContainer,
    };

    if (innerMethods?.getAvatarSvg) {
        const avatarSvg = innerMethods.getAvatarSvg(data);
        if (avatarSvg) {
            imgBox.innerHTML = avatarSvg;
            appendNewImage(appendNewImageParams);
        }
    } else if (src && srcHasPlaceholder[src]) {
        const img = new Image();
        img.src = existingPlaceholders?.[srcPlaceholders?.[src]];
        if (alt) img.alt = alt.charAt(0).toUpperCase();
        imgBox.appendChild(img);
        appendNewImage(appendNewImageParams);
    } else if (png && innerMethods.getPng) {
        imgBox.appendChild(pngElement);
        appendNewImage(appendNewImageParams);
    } else {
        const img = new Image();
        if (alt) img.alt = alt.charAt(0).toUpperCase();

        imgBox.appendChild(img);
        appendNewImage(appendNewImageParams);

        function setPlaceholder() {
            if (existingPlaceholders[placeholder]) {
                img.src = existingPlaceholders[placeholder];
                srcPlaceholders[src] = placeholder;
                srcHasPlaceholder[src] = true;
                return;
            }

            getDataUri(placeholder).then((dataUri) => {
                if (!dataUri) return;
                if (!existingPlaceholders[placeholder]) existingPlaceholders[placeholder] = dataUri;
                srcPlaceholders[src] = placeholder;
                srcHasPlaceholder[src] = true;
                img.src = dataUri;
            });
        }

        if (src) {
            img.src = existingPlaceholders[placeholder] || placeholder;
            getImageError(src)
                .then(() => {
                    // Avoiding calls to same placeholder
                    if (!existingPlaceholders[placeholder]) {
                        getDataUri(placeholder).then((dataUri) => {
                            existingPlaceholders[placeholder] = dataUri;
                        });
                    }
                    img.src = src;
                })
                .catch(setPlaceholder);
        } else {
            setPlaceholder();
        }
    }

    let tagElement = null;

    if (innerMethods?.getInfoForTag) {
        const tagInfo = innerMethods.getInfoForTag(data);
        tagElement = tagInfo && TagElement(tagInfo);
    }
    if (tagElement) {
        const wrapperElement = document.createElement('div');
        wrapperElement.classList.add('w5-avatar-cell__wrapper');
        wrapperElement.appendChild(avatarCell);
        wrapperElement.appendChild(tagElement);
        return wrapperElement;
    } else {
        return avatarCell;
    }
}

const appendNewImage = ({ imgContainer, imgBox, svgContainer, avatarCell, textContainer }) => {
    imgContainer.appendChild(imgBox);
    if (svgContainer) imgContainer.appendChild(svgContainer);
    avatarCell.appendChild(imgContainer);
    avatarCell.appendChild(textContainer);
};

const getImageError = (src) =>
    new Promise((resolve, reject) => {
        if (!src) return reject();
        requestAnimationFrame(() => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(src);
            img.onerror = reject;
            img.src = src;
        });
    });

function toDataUrl(url, callback) {
    var xhr = new XMLHttpRequest();
    xhr.onload = function () {
        var reader = new FileReader();
        reader.onloadend = function () {
            callback(reader.result);
        };
        reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
}

const getDataUri = (src) => {
    return new Promise((resolve, reject) => {
        if (!src) return resolve();
        requestAnimationFrame(() => {
            toDataUrl(src, (dataUri) => {
                resolve(dataUri);
            });
        });
    });
};
